import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Mail, Smartphone } from "lucide-react";

const LABELS = { totp: "Authenticator app", email: "Email code" };

export const MfaMethodCard = ({ status, onChanged }) => {
  const current = status?.method || "totp";
  const target = current === "totp" ? "email" : "totp";
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const run = async (fn) => {
    setErr(""); setMsg(""); setBusy(true);
    try { await fn(); } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const start = () => run(async () => {
    const { data } = await api.post("/auth/mfa/method/start", { method: target, password });
    setPending(data); setPassword("");
  });

  const confirm = () => run(async () => {
    await api.post("/auth/mfa/method/confirm", { method: target, code });
    setPending(null); setCode("");
    setMsg(`Switched to ${LABELS[target].toLowerCase()}.`);
    onChanged?.();
  });

  const Icon = current === "email" ? Mail : Smartphone;
  return (
    <section className="border border-[var(--dojo-border)] bg-[var(--dojo-paper)]" data-testid="mfa-method-card">
      <div className="px-6 py-4 border-b border-[var(--dojo-border)]">
        <h2 className="font-serif text-2xl">Sign-in Method</h2>
      </div>
      <div className="px-6 py-6 space-y-4">
        <div className="flex items-center gap-3 text-sm" data-testid="mfa-current-method">
          <Icon size={18} className="text-[var(--dojo-green)]" />
          <span>Currently using <b>{LABELS[current]}</b>{current === "email" && status?.email ? ` (${status.email})` : ""}</span>
        </div>
        {!pending ? (
          <>
            <p className="text-sm text-[var(--dojo-ink-soft)]">
              {target === "email"
                ? "Prefer a code sent to your email each time you sign in? Confirm with your password."
                : "Switch back to an authenticator app (more secure). Confirm with your password."}
            </p>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              data-testid="mfa-method-password-input"
              className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-4 py-3 text-sm focus:outline-none focus:border-[var(--dojo-ink)]"
            />
            <button onClick={start} disabled={busy || password.length < 6} className="btn-primary" data-testid="mfa-method-start-btn">
              {busy ? "Working…" : `Switch to ${LABELS[target].toLowerCase()}`}
            </button>
          </>
        ) : (
          <div className="space-y-4" data-testid="mfa-method-confirm">
            {pending.method === "totp" ? (
              <div className="flex gap-4 items-start">
                <img src={pending.qr_png} alt="Authenticator QR code" className="w-[140px] h-[140px] border border-[var(--dojo-border)] bg-white" data-testid="mfa-method-qr" />
                <div className="text-xs text-[var(--dojo-ink-soft)] space-y-2">
                  <p>Scan with your authenticator app, then enter the code it shows.</p>
                  <code className="block break-all font-mono-accent border border-[var(--dojo-border)] px-2 py-1">{pending.manual_key}</code>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[var(--dojo-ink-soft)]">We emailed a 6-digit code to {pending.email}. Enter it to confirm.</p>
            )}
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              data-testid="mfa-method-code-input"
              className="w-full border border-[var(--dojo-border)] bg-[var(--dojo-input-bg)] px-4 py-3 font-mono-accent text-xl tracking-[0.5em] text-center focus:outline-none focus:border-[var(--dojo-ink)]"
            />
            <div className="flex gap-3">
              <button onClick={confirm} disabled={busy || code.length !== 6} className="btn-primary flex-1" data-testid="mfa-method-confirm-btn">
                {busy ? "Checking…" : "Confirm switch"}
              </button>
              <button onClick={() => { setPending(null); setCode(""); setErr(""); }} className="btn-outline" data-testid="mfa-method-cancel-btn">Cancel</button>
            </div>
          </div>
        )}
        {msg && <div className="text-sm text-[var(--dojo-green)]" data-testid="mfa-method-msg">{msg}</div>}
        {err && <div className="text-sm text-[var(--dojo-hinomaru)]" data-testid="mfa-method-error">{err}</div>}
      </div>
    </section>
  );
};
